import { useState, useCallback } from 'react'
import clubService from '../services/clubService'

/**
 * Récupère à la demande une URL d'aperçu du logo club.
 *
 * Le bucket S3 logo est PRIVÉ (Block Public Access) : un <img src=logo_url>
 * direct renvoie 403. GET /club/logo-preview renvoie soit une presigned GET
 * valable 1h (logo dans notre bucket), soit l'URL externe legacy telle
 * quelle, soit null si aucun logo n'est défini.
 *
 * `fetchPreview` est volontairement explicite (pas d'auto-fetch) : la
 * presigned a un TTL court, on ne la récupère qu'au moment d'afficher
 * l'aperçu (clic "Voir l'aperçu" / refresh après upload).
 *
 * BUG-003 — cache module-level pour éviter le flicker du logo sidebar à
 * chaque clic NavLink. DashboardLayout est enveloppé par chaque page (pas
 * de <Outlet> central), donc <ClubLogoOrName> est remonté à chaque
 * navigation : sans cache partagé, chaque remount refait le fetch et
 * affiche un skeleton de transition.
 *
 * Sémantique du cache :
 *   cachedUrl === undefined  → pas encore fetch dans cette session
 *   cachedUrl === null       → fetch fait, pas de logo
 *   cachedUrl === string     → fetch fait, URL presigned valide
 *
 * Invalidation : appeler `reset()` (ex. après upload d'un nouveau logo)
 * ou `fetchPreview({ force: true })`.
 */

let cachedUrl = undefined
let pendingPromise = null

export function usePresignedLogo() {
  // Init synchrone depuis le cache si chaud — pas de skeleton au remount.
  const [previewUrl, setPreviewUrl] = useState(cachedUrl ?? null)
  const [loading, setLoading]       = useState(cachedUrl === undefined)
  const [error, setError]           = useState(null)

  const fetchPreview = useCallback(async ({ force = false } = {}) => {
    // Cache hit (et pas de refresh forcé) : retour synchrone, pas de
    // setLoading(true) → pas de flicker au remount du composant.
    if (!force && cachedUrl !== undefined) {
      setPreviewUrl(cachedUrl)
      setLoading(false)
      setError(null)
      return cachedUrl
    }

    setLoading(true)
    setError(null)

    // Dédup concurrent fetches (plusieurs ClubLogoOrName montés en parallèle,
    // ex. sidebar + hero). `force=true` bypass pour relire après upload.
    if (!pendingPromise || force) {
      pendingPromise = clubService.getLogoPreview()
        .then(r => {
          cachedUrl = r.preview_url ?? null
          return cachedUrl
        })
        .finally(() => { pendingPromise = null })
    }

    try {
      const url = await pendingPromise
      setPreviewUrl(url)
      return url
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Aperçu indisponible')
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    // Invalide le cache module + reset state local. Utilisé après upload
    // d'un nouveau logo (la presigned précédente n'est plus pertinente).
    cachedUrl = undefined
    pendingPromise = null
    setPreviewUrl(null)
    setError(null)
    setLoading(false)
  }, [])

  return { previewUrl, loading, error, fetchPreview, reset }
}
